/* eslint-disable */
// usePasswordStrength — live password policy verdict for auth screens.
// Mirrors the SERVER policy exactly: min 8 chars (Supabase Auth setting) + not
// present in the HaveIBeenPwned breach corpus — the SAME database Supabase's
// "leaked password protection" checks on updateUser/signUp. The leak check uses
// the k-anonymity range API (only the first 5 chars of the SHA-1 ever leave the
// browser — the password itself is never sent). Network failure FAILS OPEN
// (leak='unknown', submit allowed) — the server remains the enforcer of record.
import { useEffect, useRef, useState } from 'react';

const HIBP_RANGE = 'https://api.pwnedpasswords.com/range/';
const DEBOUNCE_MS = 550;

async function sha1Hex(str) {
  const buf = await crypto.subtle.digest('SHA-1', new TextEncoder().encode(str));
  return Array.from(new Uint8Array(buf)).map((b) => b.toString(16).padStart(2, '0')).join('').toUpperCase();
}

// 0..4 — display heuristic only (the verdict below is what gates submit).
export function scorePassword(pw) {
  if (!pw) return 0;
  let s = 0;
  if (pw.length >= 8) s++;
  if (pw.length >= 12) s++;
  const kinds = [/[a-z]/, /[A-Z]/, /\d/, /[^A-Za-z0-9]/].filter((r) => r.test(pw)).length;
  if (kinds >= 2) s++;
  if (kinds >= 3 && pw.length >= 10) s++;
  return Math.min(s, 4);
}

export function usePasswordStrength(password, confirm) {
  // leak: 'idle' | 'checking' | 'clean' | 'leaked' | 'unknown' (network fail-open)
  const [leak, setLeak] = useState('idle');
  const timer = useRef(null);
  const abort = useRef(null);
  const seq = useRef(0);

  useEffect(() => {
    clearTimeout(timer.current);
    if (abort.current) abort.current.abort();
    if (!password || password.length < 8) { setLeak('idle'); return; }
    setLeak('checking');
    const mySeq = ++seq.current;
    timer.current = setTimeout(async () => {
      try {
        const hash = await sha1Hex(password);
        abort.current = new AbortController();
        const res = await fetch(HIBP_RANGE + hash.slice(0, 5), {
          signal: abort.current.signal,
          headers: { 'Add-Padding': 'true' },
        });
        if (!res.ok) throw new Error('hibp ' + res.status);
        const body = await res.text();
        if (seq.current !== mySeq) return; // superseded by newer keystroke
        const suffix = hash.slice(5);
        const hit = body.split('\n').some((line) => {
          const [sfx, count] = line.trim().split(':');
          return sfx === suffix && parseInt(count, 10) > 0; // padding rows have :0
        });
        setLeak(hit ? 'leaked' : 'clean');
      } catch (e) {
        if (e.name === 'AbortError') return;
        if (seq.current !== mySeq) return;
        setLeak('unknown'); // fail-open — server still enforces
      }
    }, DEBOUNCE_MS);
    return () => clearTimeout(timer.current);
  }, [password]);

  const score = scorePassword(password);
  const lenOk = password.length >= 8;
  const varietyOk = [/[a-zA-Z]/, /\d/, /[^A-Za-z0-9]/].filter((r) => r.test(password)).length >= 2;
  const matchOk = !!password && password === confirm;
  const leakBlocks = leak === 'leaked' || leak === 'checking';
  const canSubmit = lenOk && matchOk && !leakBlocks;

  let verdict = 'none'; // none | wait | willPass | needConfirm | tooShort | leaked
  if (password) {
    if (leak === 'checking') verdict = 'wait';
    else if (leak === 'leaked') verdict = 'leaked';
    else if (!lenOk) verdict = 'tooShort';
    else if (!matchOk) verdict = 'needConfirm';
    else verdict = 'willPass';
  }

  return { score, lenOk, varietyOk, matchOk, leak, verdict, canSubmit };
}

// Memorable-but-unique suggestion: Word-Word!NN — never a breached pattern.
const W1 = ['Amber', 'Velvet', 'Copper', 'Marble', 'Cedar', 'Indigo', 'Basil', 'Pearl', 'Falcon', 'Nova'];
const W2 = ['Lens', 'Frame', 'Bridge', 'Prism', 'Optic', 'Shade', 'Vista', 'Tide', 'Ember', 'Cloud'];
export function suggestPassword() {
  const n = 10 + Math.floor(Math.random() * 89);
  return `${W1[Math.floor(Math.random() * W1.length)]}-${W2[Math.floor(Math.random() * W2.length)]}!${n}`;
}
